// ================================================
// API Request Manager V1 - Centralized Request Coordination
// Solves Rate Limiting Issues with Sequential & Prioritized Requests
// ================================================

interface QueuedRequest {
    id: string;
    execute: () => Promise<any>;
    priority: number; // 1 = highest, 3 = lowest
    resolve: (value: any) => void;
    reject: (error: any) => void;
    timestamp: number;
    description: string;
    retryCount?: number; // Track retry attempts
    maxRetries?: number; // Maximum retry attempts
}

interface RequestStats {
    total: number;
    success: number;
    failed: number;
    rateLimited: number;
    lastRequest: number;
}

class ApiRequestManager {
    private queue: QueuedRequest[] = [];
    private processing = false;
    private minInterval = 1200; // 1.2 seconds between requests (safe for 60/min limit)
    private lastRequestTime = 0;
    private stats: RequestStats = {
        total: 0,
        success: 0,
        failed: 0,
        rateLimited: 0,
        lastRequest: 0
    };

    // Circuit breaker for extreme rate limiting
    private circuitBreakerActive = false;
    private circuitBreakerUntil = 0;
    private consecutiveRateLimits = 0;
    private maxConsecutiveRateLimits = 3;
    private wasRateLimited = false; // Track if we've been rate limited

    constructor() {
        // Initialized silently
    }

    private emitEvent(type: string, details: any = {}) {
        const event = new CustomEvent('apiManagerEvent', {
            detail: { type, details }
        });
        window.dispatchEvent(event);
    }

    // Add request to queue with priority
    async enqueue<T>(
        executeRequest: () => Promise<T>,
        priority: number = 2,
        description: string = 'Unknown request',
        maxRetries: number = 2 // Default: max 2 retries for 429 errors
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            const request: QueuedRequest = {
                id: this.generateId(),
                execute: executeRequest,
                priority,
                resolve,
                reject,
                timestamp: Date.now(),
                description,
                retryCount: 0,
                maxRetries
            };

            // Insert by priority (lower number = higher priority)
            const insertIndex = this.queue.findIndex(r => r.priority > priority);
            if (insertIndex === -1) {
                this.queue.push(request);
            } else {
                this.queue.splice(insertIndex, 0, request);
            }



            // Start processing if not already running
            this.processQueue();
        });
    }

    // Process queue sequentially with rate limiting
    private async processQueue(): Promise<void> {
        if (this.processing || this.queue.length === 0) {
            return;
        }

        this.processing = true;

        while (this.queue.length > 0) {
            const request = this.queue.shift()!;

            // Circuit breaker check
            const now = Date.now();
            if (this.circuitBreakerActive && now < this.circuitBreakerUntil) {
                const waitTime = this.circuitBreakerUntil - now;

                // Emit event for UI notification
                this.emitEvent('circuit_breaker_opened', {
                    waitTime,
                    consecutiveErrors: this.consecutiveRateLimits,
                    description: request.description
                });

                request.reject(new Error('Circuit breaker active - too many rate limits'));
                continue;
            } else if (this.circuitBreakerActive && now >= this.circuitBreakerUntil) {
                // Reset circuit breaker
                this.circuitBreakerActive = false;
                this.consecutiveRateLimits = 0;
            }

            // Rate limiting: ensure minimum interval between requests
            const timeSinceLastRequest = now - this.lastRequestTime;

            if (timeSinceLastRequest < this.minInterval) {
                const waitTime = this.minInterval - timeSinceLastRequest;
                await this.delay(waitTime);
            }

            try {

                const result = await request.execute();
                this.lastRequestTime = Date.now();
                this.stats.lastRequest = this.lastRequestTime;
                this.stats.total++;
                this.stats.success++;

                // Check if we were rate limited and now recovered
                if (this.wasRateLimited && this.consecutiveRateLimits > 0) {
                    // Emit recovery event
                    this.emitEvent('rate_limit_resolved', {
                        description: request.description,
                        recovery: true
                    });
                }

                // Reset consecutive rate limits on success
                this.consecutiveRateLimits = 0;
                this.wasRateLimited = false; // Reset rate limit flag

                request.resolve(result);

            } catch (error: any) {
                this.stats.total++;
                this.stats.failed++;

                // Check if it's a rate limiting error
                if (error?.message?.includes('429') || error?.status === 429) {
                    this.stats.rateLimited++;
                    this.consecutiveRateLimits++;
                    this.wasRateLimited = true; // Mark that we've been rate limited

                    // Emit rate limiting event
                    const retryDelay = 8000; // Base retry delay for 429 errors
                    this.emitEvent('rate_limited', {
                        retryDelay,
                        retryCount: request.retryCount || 0,
                        consecutiveErrors: this.consecutiveRateLimits,
                        description: request.description
                    });

                    // Activate circuit breaker if too many consecutive rate limits
                    if (this.consecutiveRateLimits >= this.maxConsecutiveRateLimits) {
                        this.circuitBreakerActive = true;
                        this.circuitBreakerUntil = Date.now() + (30000); // 30 seconds circuit breaker

                        request.reject(new Error('Circuit breaker activated - too many rate limits'));
                        continue;
                    }

                    const currentRetryCount = (request.retryCount || 0) + 1;
                    const maxRetries = request.maxRetries || 1; // Reduced default retries

                    if (currentRetryCount <= maxRetries) {
                        // Exponential backoff: 8s, 16s, 32s (longer for better recovery)
                        const backoffDelay = Math.min(8000 * Math.pow(2, currentRetryCount - 1), 60000);

                        // Emit many retries event if this is the 2nd+ retry
                        if (currentRetryCount >= 2) {
                            this.emitEvent('many_retries', {
                                retryCount: currentRetryCount,
                                maxRetries,
                                description: request.description,
                                backoffDelay
                            });
                        }



                        await this.delay(backoffDelay);

                        // Re-queue with updated retry count
                        this.queue.unshift({
                            ...request,
                            priority: 1, // High priority for retry
                            timestamp: Date.now(),
                            retryCount: currentRetryCount
                        });
                        continue;
                    } else {
                        // Max retries exceeded
                        if (process.env.NODE_ENV === 'development') {
                            console.error(`❌ Max retries exceeded for: ${request.description} (${currentRetryCount}/${maxRetries})`);
                        }
                        request.reject(new Error(`Rate limited - max retries (${maxRetries}) exceeded`));
                    }
                } else {
                    // Reset consecutive rate limits on non-429 error
                    this.consecutiveRateLimits = Math.max(0, this.consecutiveRateLimits - 1);
                }

                if (process.env.NODE_ENV === 'development') {
                    console.error(`❌ Failed: ${request.description}`, error.message);
                }

                request.reject(error);
            }
        }

        this.processing = false;
    }

    // Get queue status
    getStatus() {
        const now = Date.now();
        return {
            queueLength: this.queue.length,
            processing: this.processing,
            stats: { ...this.stats },
            nextRequestIn: Math.max(0, this.minInterval - (now - this.lastRequestTime)),
            circuitBreaker: {
                active: this.circuitBreakerActive,
                remainingTime: this.circuitBreakerActive ? Math.max(0, this.circuitBreakerUntil - now) : 0,
                consecutiveRateLimits: this.consecutiveRateLimits
            }
        };
    }

    // Clear queue (emergency)
    clearQueue() {
        const clearedCount = this.queue.length;
        this.queue.forEach(request => {
            request.reject(new Error('Request cancelled - queue cleared'));
        });
        this.queue = [];

        if (process.env.NODE_ENV === 'development') {
            console.log(`🧹 Cleared ${clearedCount} queued requests`);
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private generateId(): string {
        return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
}

// Singleton instance
export const apiRequestManager = new ApiRequestManager();

// Helper function for easy usage
export const queueApiRequest = <T>(
    request: () => Promise<T>,
    priority: number = 2,
    description: string = 'API Request',
    maxRetries: number = 1 // Reduced default to prevent retry storms
): Promise<T> => {
    return apiRequestManager.enqueue(request, priority, description, maxRetries);
};

// Priority constants
export const REQUEST_PRIORITY = {
    CRITICAL: 1,    // Authentication, security
    HIGH: 2,        // User actions, core features  
    NORMAL: 3,      // Background updates, non-critical
} as const;

export default apiRequestManager;
